import {
  commitUpdateIssueIssueTypeMutation,
  type OptimisticIssueType,
} from '@github-ui/item-picker/commitUpdateIssueIssueTypeMutation'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {IssueTypePicker} from '@github-ui/item-picker-issue-type-tsq'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import type {SxProp} from '@primer/styled-react'
import {useCallback, useMemo} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {TypesSectionFragment$key} from './__generated__/TypesSectionFragment.graphql'
import type {TypesSectionTypeFragment$key} from './__generated__/TypesSectionTypeFragment.graphql'
import {IssueType} from './IssueType'
import styles from './TypesSection.module.css'

const ReadonlyTypesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.types} />

type TypesSectionProps = {
  sectionHeader: React.ReactElement
  type: IssueTypePickerIssueType | null
  repoNameWithOwner?: string
} & SxProp

const TypesSection = ({sectionHeader, type, repoNameWithOwner, sx}: TypesSectionProps) => (
  <Section
    id={`sidebar-types-section`}
    sx={sx}
    sectionHeader={sectionHeader}
    emptyText={type ? undefined : LABELS.emptySections.types}
  >
    <div className={styles.typeContainer}>
      <IssueType type={type} repoNameWithOwner={repoNameWithOwner} />
    </div>
  </Section>
)

export type CreateIssueIssueTypesSectionProps = {
  repo: string
  owner: string
  type: IssueTypePickerIssueType | null
  onSelectionChange: (type: IssueTypePickerIssueType | null) => void
  viewerCanType: boolean
  insidePortal: boolean
} & SxProp
export function CreateIssueIssueTypesSection({
  type,
  onSelectionChange,
  viewerCanType,
  sx,
  ...sharedConfigProps
}: CreateIssueIssueTypesSectionProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const sectionHeader = () => {
    if (!viewerCanType) {
      return <ReadonlyTypesSectionHeader />
    } else {
      return (
        <IssueTypePicker
          onSelectionChange={newType => {
            const issueType = newType?.[0] || null
            sendAnalyticsEvent('issue_create.update_issue_type', 'ISSUE_CREATE_UPDATE_ISSUE_TYPE_DIALOG', {
              issueTypeId: issueType?.id ?? '',
            })
            onSelectionChange(issueType)
          }}
          readonly={viewerCanType}
          activeIssueType={type}
          anchorElement={(anchorProps, ref) => (
            <SectionHeader title={LABELS.sectionTitles.types} buttonProps={anchorProps} ref={ref} />
          )}
          {...sharedConfigProps}
          width="medium"
          showPinnedFieldsCount
        />
      )
    }
  }

  const {repo, owner} = sharedConfigProps

  return <TypesSection sx={sx} sectionHeader={sectionHeader()} type={type} repoNameWithOwner={`${owner}/${repo}`} />
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TypesSectionTypeFragment = graphql`
  fragment TypesSectionTypeFragment on Issue {
    issueType {
      id
      name
      isEnabled
      description
      color
      pinnedFields {
        __typename
        ... on IssueFieldText {
          id
          name
          dataType
        }
        ... on IssueFieldSingleSelect {
          id
          name
          dataType
        }
        ... on IssueFieldDate {
          id
          name
          dataType
        }
        ... on IssueFieldNumber {
          id
          name
          dataType
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TypesSectionFragment = graphql`
  fragment TypesSectionFragment on Issue {
    repository {
      name
      nameWithOwner
      owner {
        login
      }
      issueTypes(first: 100) {
        edges {
          node {
            id
          }
        }
      }
    }
    id
    ...TypesSectionTypeFragment
    viewerCanType
  }
`

type EditIssueIssueTypesSectionProps = {
  issue: TypesSectionFragment$key
  onIssueUpdate?: () => void
  insideSidePanel?: boolean
  readonly: boolean
}
export function EditIssueIssueTypeSection({
  issue,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueIssueTypesSectionProps) {
  const data = useFragment(TypesSectionFragment, issue)
  const {
    repository: {
      name: repo,
      owner: {login: owner},
      nameWithOwner,
      issueTypes,
    },
    id: issueId,
    viewerCanType,
  } = data

  const formattedIssueTypes = issueTypes?.edges?.map(edge => edge?.node?.id) || []

  const {issueType} = useFragment<TypesSectionTypeFragment$key>(TypesSectionTypeFragment, data)

  const activeType = issueType || null

  const {sendAnalyticsEvent} = useAnalytics()
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const onSelectionChanged = useCallback(
    (selectedTypes: IssueTypePickerIssueType[]) => {
      const selectedType = selectedTypes.length > 0 ? selectedTypes[0] : null
      const issueTypeId = selectedType?.id ?? null
      sendAnalyticsEvent('issue_viewer.update_issue_type', 'ISSUE_VIEWER_UPDATE_ISSUE_TYPE_DIALOG', {
        initialIssueTypeId: activeType?.id ?? '',
        issueTypeId: issueTypeId ?? '',
      })

      // Build optimistic issue type from the selected type for instant UI update
      const optimisticIssueType: OptimisticIssueType | null = selectedType
        ? {
            id: selectedType.id,
            name: selectedType.name,
            description: selectedType.description,
            color: selectedType.color,
            isEnabled: selectedType.isEnabled,
            pinnedFields: selectedType.pinnedFields,
          }
        : null

      commitUpdateIssueIssueTypeMutation({
        environment,
        input: {issueId, issueTypeId},
        optimisticIssueType,
        onError: () =>
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateType,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [activeType?.id, addToast, environment, issueId, onIssueUpdate, sendAnalyticsEvent],
  )

  const sectionHeader = useMemo(() => {
    const readonlyType = readonly || !viewerCanType
    if (readonlyType) {
      return <ReadonlyTypesSectionHeader />
    }

    return (
      <IssueTypePicker
        owner={owner}
        repo={repo}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader title={LABELS.sectionTitles.types} buttonProps={anchorProps} ref={ref} />
        )}
        readonly={readonlyType}
        activeIssueType={activeType ?? null}
        insidePortal={insideSidePanel}
        width="medium"
        showPinnedFieldsCount
      />
    )
  }, [readonly, viewerCanType, owner, repo, onSelectionChanged, activeType, insideSidePanel])

  if (!viewerCanType && formattedIssueTypes.length === 0) return null

  return <TypesSection sectionHeader={sectionHeader} type={activeType ?? null} repoNameWithOwner={nameWithOwner} />
}
